import { LightningElement, track, api } from 'lwc';
import { publicPropertyToBool } from '../../../utils/basicUtils';

import { getLabelsByLanguage } from '../../../utils/languageUtil';
import { strToInt } from '../../../utils/dtypeUtils';


/* Example Usage:

    Example 1 (Static Variant):

        • HTML:
            <x-slds-input 
                dtype="text/textarea/number/currency/percent/date/datetime/decimal" 
                label="label above the input element" 
                place-holder="place holder for the input element"
                data-id={myDataId} ( optional --> no effect on <x-slds-input> )
                is-required="true/false"
                onchange={myInputHandler}
            ></x-slds-input>

        • Javascript:
            myInputHandler(message) {
                // if you added a data-id...
                const fieldName = event.currentTarget.dataset.id;

                // Value sent from <x-slds-input>
                const value = event.detail.value;

                // do something with the value...  
                // ---> e.g. trigger an error if invalid
                const isError = this.fieldsObj[fieldName].isRequired && !val;

                if (isError) {
                    // ---> e.g. trigger an error in <x-slds-input>
                    this.template.querySelector(`[data-id="${myDataId}"]`).errorMe();

                } else {
                    // ---> Form e.g.) save the data if a form
                    this.fieldsObj[fieldName].value = value;
                    this.saveData(this.fieldsObj)

                    // ---> Popover e.g.) if is a popover, close the popover
                    this.draftValues.push({fieldName: fieldName, value: value});
                    this.closePopover();
                }
            }

    Example 2 (Popover Variant):
        ** is-popover="true" changes behavior on Tab/Escape  
        
        • HTML:
            <x-slds-input 
                dtype="number" 
                label="" 
                place-holder=""
                is-required="false"
                onescape={inputEleBlur}       -->  Trigger <x-slds-input> to send event on Escape key (used with Popover variant so parent knows to close popover)
                is-popover="true"             -->  Set to True if is Popover
                current-value={popoverEdit.value}   
                onchange={handleInputMessage}
            ></x-slds-input>

        • Javascript:
            // Example popover details
            myPopoverObj = { value: '', label: '' };
            
            // Get the value sent by the child component (<x-slds-input> / <x-dropdown-plain> / <x-slds-rich-text-editor>)
            handleInputMessage(event) {
                const details = event.detail;

                // message is in <event.detail.value> if valid value
                const isValidValue = typeof details === 'object';

                if (!isValidValue) {
                    // Close the Popover
                    this.closePopover();
                } else {
                    // Update the Popover
                    this.myPopoverObj.value = details[Object.keys(details)[0]];
                    this.myPopoverObj.label =  `${details[Object.keys(details)[0]]}`;
                }

            // Close Popover on Escape
            inputEleBlur(message) {
                if (message) this.closePopover();
            }
                                           

*/

const NUMERIC_DTYPES = new Set(['decimal','double','currency','integer','long','percent','number']);
const BOOL_DTYPES = new Set(['boolean']);
const DATE_DTYPES = new Set(['date','datetime']);
const MIN_MAX_MAP = {
    number: 'numberInput',
    currency: 'currencyInput',
    decimal: 'decimalInput',
    percent: 'percentInput',
};
const ADDON_CL = 'slds-input-has-fixed-addon';
const IS_ADDON_COMPATIBLE = (dtype) => {
    return Object.keys(FORM_ELEMENT_CONTROL_BASE_CLS).some(key => key === dtype);
}
const GET_ADDON_ID = (dtype, addon, isRight=false) => {
    if (!addon) return '';
    return isRight 
        ? `fixed-text-${dtype}-right`
        : `fixed-text-${dtype}-left`;
}
const FORM_ELEMENT_CONTROL_BASE_CLS = {
    number: 'slds-form-element__control',
    currency: 'slds-form-element__control slds-input-has-icon slds-input-has-icon_left',
    decimal: 'slds-form-element__control',
    percent: 'slds-form-element__control slds-input-has-fixed-addon',
};
// Check for Addons & suppression of default addons/icons
function handleFormElementCl(dtype, addonLeft, addonRight, suppressDefaultStyles) {

    const defaultCl = suppressDefaultStyles ? 'slds-form-element__control' 
        : FORM_ELEMENT_CONTROL_BASE_CLS[dtype] ?? 'slds-form-element__control';


    if (suppressDefaultStyles && !addonLeft && !addonRight) {
        return 'slds-form-element__control';
    }
    if (!suppressDefaultStyles && !addonLeft && !addonRight) {
        return defaultCl;
    }

    return `${defaultCl} ${addonLeft || addonRight  ? ADDON_CL : ''}`;
}
function handleAriaLabellBy(dtype, addonLeft, addonRight, suppressDefaultStyles) {
    const notHasDefault = dtype !== 'percent'; 

    if (notHasDefault || suppressDefaultStyles) {
        return `${GET_ADDON_ID(dtype, addonRight, true)} ${GET_ADDON_ID(dtype, addonLeft)}`;
    } 

    const addonRightId = GET_ADDON_ID(dtype, addonRight, true);
    const addonLeftId = GET_ADDON_ID(dtype, addonLeft, false);

    return `${addonRightId ?? 'fixed-text-addon-pre'} ${addonLeftId}`; 
}
function handleFormElement(cmp, dtype, addonLeft, addonRight, suppressDefaultStyles) {
    try {
        const cl = handleFormElementCl(dtype, addonLeft, addonRight, suppressDefaultStyles);
        const ariaLabel = handleAriaLabellBy(dtype, addonLeft, addonRight, suppressDefaultStyles) ?? null;
        return {cl, ariaLabel};
    } catch(error) {
        cmp.showToast('Error in handleFormElement() of sldsInput.js', error, 'error');
    } 
}
function handleInputCl(suppressDefaultStyles, suppressNativeStyles) {
    return suppressDefaultStyles || suppressNativeStyles
        ? 'slds-input slds-input_counter slds-text-align_left'
        : 'slds-input';
}
function handleFormElementControlErrorIcon(formElementCls, hasError, errorMessage) {
    if (hasError || errorMessage) {
        return formElementCls.includes('slds-input-has-icon_left') 
            ? `${formElementCls} slds-input-has-icon_group-left`
                : formElementCls.includes('slds-input-has-icon_right') 
            ? formElementCls.replace('slds-input-has-icon_right', 'slds-input-has-icon_left-right')
            : `${formElementCls} slds-input-has-icon slds-input-has-icon_left`;
    }
    return formElementCls;
}

export default class sldsInput extends LightningElement {
    
    // ==================== Local Tracked Properties ==================== \\
    // Sync the time and the date when dtype='datetime'
    @track datetimeSyncVal;
    // Flag missing values when isRequired=true
    @track hasError=false;  
    // Set is{dtype} flag on the data type (e.g. isdecmial) 
    @track boolObj = {
        istext: false,
        isboolean: false,
        istextarea: false,
        isnumber: false,
        iscurrency: false,
        ispercent: false,
        isdate: false,
        isdatetime: false,
        isdecimal: false,
        ispassword: false,
    }
    // Set a 300ms timeout between Custom Events
    @track debounceTimeout = null;
    // For popovers watch out for 'Tab' keydown
    @track isPopoverTab = false;
    @track isDateEnter = false;
    @track mostRecentKey;
    @track originalDate;
    @track _preventBlur = false;
    // =================================================================== \\


    // ======================== Public Properties ======================== \\
    @api blockPaste = false;
    @api 
    set min(val) {
        this._min = val || val === 0 ? strToInt(val) : null;
        console.log('here is min in sldsInput.js: ', this._min);
    }
    get min() {
        return this._min ?? null;
    }
    @api 
    set max(val) {
        this._max = val ? strToInt(val) : null;;
        console.log('here is _max in sldsInput.js: ', this._max);
    }
    get max() {
        return this._max ?? null;
    }

    @api // data type of the input element
    set dtype(value) {
        this._dtype = value ? value : 'text';
        this.boolObj[`is${value}`] = true;
        this.handleInputIconAndAddons();
    }
    get dtype() {
        return this._dtype;
    }

    @api // Add red required asterik if true 
    set isRequired(val) {this._isRequired = publicPropertyToBool(val);} 
    get isRequired() {return this._isRequired;}

    // Disable a field
    @api set isDisabled(val) {this._isDisabled = publicPropertyToBool(val);}
    get isDisabled() {return this._isDisabled || false;}

    @api // Change the event handling if is a popover
    set isPopover(val) {
        this._isPopover = publicPropertyToBool(val);
    } 
    get isPopover() {
        return this._isPopover;
    }
    @track _errorMessage;
    @api // For when parent has a getter to display an error message
    set errorMessage(val) {
        this._errorMessage = val;
        this.hasError = val ? true : false;
    }
    get errorMessage() {return this._errorMessage}
    @api // Set to true to prevent close event on Tab keydown
    set isTabbable(val) { this._isTabbable = publicPropertyToBool(val); }
    get isTabbable() { return this._isTabbable; }
    // Label Above the input element
    @api 
    set label(val) {
        this._label = getLabelsByLanguage(val) ?? val;
    }
    get label() {
        return this._label;
    }
    // placeholder of input element  
    @api 
    set placeHolder(val) {
        this._placeHolder = getLabelsByLanguage(val) ?? val;
    }
    get placeHolder() {
        return this._placeHolder;
    }      
    _currentValue;
    @api 
    set currentValue(val) {
        this._currentValue = val;
    }
    get currentValue() {
        return this._currentValue;
    }
    // =================================================================== \\
    _addOnFormElement = {
        cl: 'slds-form-element__control',
        ariaLabel: null,
    }
    @api 
    set suppressIcon(val) {
        this._suppressIcon = val;
        if (val) {
            this._addOnFormElement = {
                cl: 'slds-form-element__control',
                ariaLabel: null
            }
        }

    }
    get suppressIcon() {
        return this._suppressIcon;
    }
    @api 
    set fontSize(value) {
        this._fontSize = value;
        if (value) this._applyVarsDirect('--input-font-size', value);
    } 
    get fontSize() {
        return this._fontSize;
    }
    @api 
    set inputHeight(val) {
        this._inputHeight = val;
        if (val) this._applyVarsDirect('--input-height', val);
    }
    get inputHeight() {
        return this._inputHeight;
    }

    _inputCl = 'slds-input';
    @api 
    set suppressNativeStyles(value) {
        this._suppressNativeStyles = value;
        this.handleInputIconAndAddons();
    } 
    get suppressNativeStyles() {
        return this._suppressNativeStyles ?? false;
    }
    
    @api 
    set addonLeft(value) {
        this._addonLeft = value
        if (value) {
            this.handleInputIconAndAddons();
        }
    } 
    get addonLeft() {
        return this._addonLeft;
    }
    @api 
    set addonRight(value) {
        this._addonRight = value;
        if (value) {
            this.handleInputIconAndAddons();
        }
    } 
    get addonRight() {
        return this._addonRight;
    }

    
    // =============================== Border/Border Radius =============================== \\
    @api  // Main Border Radius
    set borderRadius(val) {
        this._borderRadius = val;
        if (val) {
            const bordersArr = [
                ['--input-border-top-left-radius', val],
                ['--input-border-top-right-radius', val],
                ['--input-border-bottom-left-radius', val],
                ['--input-border-bottom-right-radius', val]
            ]
            this._applyVarsDirect(bordersArr);
        }
    }
    get borderRadius() {
        return this._borderRadius;
    }
    @api // Suppress all border
    set suppressBorder(val) {
        this._suppressBorder = val;
        if (val) {
            const borders = [
                ['--input-border-bottom', 'none'],
                ['--input-border-top', 'none'],
                ['--input-border-left', 'none'],
                ['--input-border-right', 'none'],
                ['--input-border', 'none'],
            ]
            this._applyVarsDirect(borders);
        }
    }
    get suppressBorder() {
        return this._suppressBorder ?? false;
    }   

    @api 
    set topLeftRadius(val) {
        this._topLeftRadius = val;
        if (val) {
            this._applyVarsDirect('--input-border-top-left-radius', val);
        }
    }
    get topLeftRadius() {
        return this._topLeftRadius;
    }
    @api 
    set topRightRadius(val) {
        this._topRightRadius = val;
        if (val) {
            this._applyVarsDirect('--input-border-top-right-radius', val);
        }
    }
    get topRightRadius() {
        return this._topRightRadius;
    }
    @api 
    set bottomRightRadius(val) {
        this._bottomRightRadius = val;
        if (val) {
            this._applyVarsDirect('--input-border-bottom-right-radius', val);
        }
    }
    get bottomRightRadius() {
        return this._bottomRightRadius;
    }
    @api 
    set bottomLeftRadius(val) {
        this._bottomLeftRadius = val;
        if (val) {
            this._applyVarsDirect('--input-border-bottom-left-radius', val);
        }
    }
    get bottomLeftRadius() {
        return this._bottomLeftRadius;
    }


    @api 
    set suppressBorderBottom(val) {
        this._suppressBorderBottom = val;
        if (val) {
            this._applyVarsDirect('--input-border-bottom', 'none');
        }
    }
    get suppressBorderBottom() {
        return this._suppressBorderBottom ?? false;
    }
    @api 
    set suppressBorderTop(val) {
        this._suppressBorderTop = val;
        if (val) {
            this._applyVarsDirect('--input-border-top', 'none');
        }
    }
    get suppressBorderTop() {
        return this._suppressBorderTop ?? false;
    }
    @api 
    set suppressBorderRight(val) {
        this._suppressBorderRight = val;
        if (val) {
            this._applyVarsDirect('--input-border-left', 'none');
        }
    }
    get suppressBorderRight() {
        return this._suppressBorderRight ?? false;
    }
    @api 
    set suppressBorderLeft(val) {
        this._suppressBorderLeft = val;
        if (val) {
            this._applyVarsDirect('--input-border-right', 'none');
        }
    }
    get suppressBorderLeft() {
        return this._suppressBorderLeft ?? false;
    }
    @api 
    set borderTopLeftRadius(val) {
        this._borderTopLeftRadius = val;
        if (val) {
            this._applyVarsDirect('--input-border-top-left-radius', 'none');
        }
    }
    get borderTopLeftRadius() {
        return this._borderTopLeftRadius;
    }
    @api 
    set borderTopRightRadius(val) {
        this._borderTopRightRadius = val;
        if (val) {
            this._applyVarsDirect('--input-border-top-right-radius', 'none');
        }
    }
    get borderTopRightRadius() {
        return this._borderTopRightRadius;
    }
    @api 
    set borderBottomLeftRadius(val) {
        this._borderBottomLeftRadius = val;
        if (val) {
            this._applyVarsDirect('--input-border-bottom-left-radius', 'none');
        }
    }
    get borderBottomLeftRadius() {
        return this._borderBottomLeftRadius;
    }
    @api 
    set borderBottomRightRadius(val) {
        this._borderBottomRightRadius = val;
        if (val) {
            this._applyVarsDirect('--input-border-bottom-right-radius', 'none');
        }
    }
    get borderBottomRightRadius() {
        return this._borderBottomRightRadius;
    }
    // ==================================================================================== \\

    handleInputIconAndAddons()  {
        try {
            if (!IS_ADDON_COMPATIBLE(this.dtype)) return;
            this._addOnFormElement = handleFormElement(
                this.dtype, 
                this.addonLeft, 
                this.addonRight, 
                this.suppressDefaultStyles
            );
            console.log('here is addonFormElement in handleInputIconAndAddons(): ', JSON.stringify(this._addOnFormElement));
            this._inputCl = handleInputCl(this.suppressDefaultStyles, this.suppressNativeStyles);
        } catch(error) {
            this.showToast('Error in handleInputIconAndAddons() of sldsInput.js', error, 'error');
        } 
    }

    renderedCallback() {
        if (this.boolObj.isdatetime || this.boolObj.isdate) {
            this.originalDate  = this.boolObj.isdatetime
                ? this.syncDatetimeManual()
                : this.getInputEle().value;
        }
        if (this.boolObj.isboolean) {
            if (this._currentValue === false || this._currentValue === true) {
                this.template.querySelector('[data-id="booleanInput"]').checked = this._currentValue;
            }
        }     
    }

    // ========================= Public Methods ========================== \\
    // Return the current value to the parent
    @api getValue() {
        if (this.boolObj.istextarea ||  this.boolObj.istext) {
            return this.textVal
        }
        if (this.boolObj.isdatetime) {
            return this.syncDatetimeManual();
        }
        if (this.dtype === 'percent') {
            return this.convertPercentField()
        }
        return this._currentValue; 
    }
    // Send this._currentValue to requester
    @api sendValues() {
        if (this.boolObj.istextarea ||  this.boolObj.istext) {
            return this.textVal
        } 
        if (this.boolObj.isdatetime) {
            return this.syncDatetimeManual();
        }
        if (this.dtype === 'percent') {
            return this.convertPercentField()
        }

        return this._currentValue; 
    }
    // Flag errors with the input (set aria-invalid to true)
    @api errorMe(errMsg=null) {
        if (errMsg) {
            this._errorMessage = errMsg;
        } else {
            this._errorMessage = 'This field is required.';
        }
        this.hasError=true; 
    }
    // Set <this.hasError> to false
    @api removeErrorMe() {
        this.hasError=false
        if (this._errorMessage) this._errorMessage = null;
    }
    // Add focus to the input element
    @api focusMe() {
        try {
            const ele = this.getInputEle();
            if (ele?.dataset?.id === 'dateInput') {
                const dateEle = ele.date;
                dateEle.focus();
            } else {
                ele.focus();
            }
        } catch(error) {
            this.showToast('Error in focusMe() of sldsInput.js', error, 'error');
        } 
    }
    // =================================================================== \\

    // Add slds-has-error to .slds-form-element if there is an error
    get formElementClass() {
        if (this.hasError) {
            if (this.boolObj.istextarea || this.boolObj.isboolean) {
                const ele = this.boolObj.istextarea ? 'textareaInput' : 'booleanInput';
                const input = this.template.querySelector(`[data-id="${ele}"]`);
                if (input) {
                    input.setAttribute("aria-describedby", "error-message-unique-id");
                }
                
            }
            return 'slds-form-element slds-has-error';
        } 
        // Remove error classes if no error
        if ((this.boolObj.istextarea || this.boolObj.isboolean) && this.hasError && this._currentValue) {
            const ele = this.boolObj.istextarea ? 'textareaInput' : 'booleanInput';
            const input = this.template.querySelector(`[data-id="${ele}"]`);
            if (input) input.removeAttribute("aria-describedby");
        }
        return 'slds-form-element'; 
    }
    get dateTimeGridClass() {
        return this.isPopover
            ? 'slds-grid date-padding'
            : 'slds-grid';
    }
    get dateGridClass() {
        return this.isPopover
            ? {base: 'slds-grid', col: 'slds-form-element__control slds-col'}
            : {base: '', col: 'slds-form-element__control'}
    }
    // ================ Form Input Parent Event Handlers ================= \\
    // Return the current input element based on the <dtype>
    getInputEle() {
        const dtype = this._dtype ? this._dtype : 'text';

        switch(dtype) {
            case 'number':
                return this.template.querySelector('[data-id="numberInput"]');
                
            case 'currency':
                return this.template.querySelector('[data-id="currencyInput"]');
                
            case 'decimal':
                return this.template.querySelector('[data-id="decimalInput"]');
                
            case 'percent':
                return this.template.querySelector('[data-id="percentInput"]');

            case 'date':
                return this.template.querySelector('[data-id="dateInput"]');
            
            case 'textarea': 
                return this.template.querySelector('[data-id="textareaInput"]'); 

            case 'datetime':
                const datetimeDateInp = this.template.querySelector('[data-id="datetimeInputDate"]');
                const datetimeTimeInp = this.template.querySelector('[data-id="datetimeInputTime"]');
                return {date: datetimeDateInp, time: datetimeTimeInp};

            case 'password':
                return this.template.querySelector('[data-id="passwordInput"]');

            default:            
                return this.template.querySelector('[data-id="textInput"]');      
        }
    }
    // Convert the whole number to a decimal for percents
    convertPercentField() {
        // turn "50", ".5", "1.5999", etc. into a Number
        const raw = parseFloat(this.numberVal);
        if (isNaN(raw)) {
            return '0.00';               // fallback for empty/bad input
        }
        // scale it into 0–1
        const pct = raw / 100;
        // round and pad to exactly two decimals
        return pct.toFixed(2);         // e.g. "0.50", "0.06", "1.00"
    }
    // Handle regular input Value Change
    handleInputEleChange(event) {
        try  {
            if (this.isPopover) {
                // Bounce event to popover input change handler is <isPopover>
                return;
            }
            
            // Update the value
            if (!this.boolObj.isdate && !this.boolObj.isdatetime) {
                this._currentValue = `${event.target.value}`;
            } else {
                this._currentValue = event.target.value;
            }

            // Get the input element
            const element = this.getInputEle();

            // Get the current value from the input element
            let val = this.dtype === 'datetime' 
                ? this.syncDatetimeManual() 
                : element.value ?? this._currentValue; 

            

            if (this.hasError && val) {
                // Remove error if the val is not null anymore
                this.hasError = false;
            }

            // Clear previous timeout if it exists
            clearTimeout(this.debounceTimeout);

            // Send the parent notification that the value was updated
            this.debounceTimeout = setTimeout(() => {
                if (this.dtype === 'percent') {
                    // convert to 2-decimal
                    val = this.convertPercentField();
                } 

                // Send event to parent with the value
                this.dispatchEvent(new CustomEvent('change', { detail: {value: val} } ));

            }, 300);

        }catch(error) {
            console.error('handleEditInputChange', error);
        }
    }
    handleCheckboxChange(event) {
        this._currentValue = event.currentTarget.checked;
        this.dispatchEvent(new CustomEvent('change', { detail: {value: this._currentValue} } ));
    }
    // =================================================================== \\


    // ================= Popover Parent Event Handlers =================== \\
    // **--Popovers Only--** detect 'Tab', 'Escape'
    handlePopoverKeyDown(event) {
        if (!this.isPopover) return;
        this.mostRecentKey = event.key;
        event.stopPropagation();

        if (event.key === 'Enter') {
            if (this.boolObj.isdate || this.boolObj.isdatetime)  {
                return;
            }
            
            event.preventDefault();

            const val = this.boolObj.isdatetime 
                ? this.syncDatetimeManual() 
                    : this.boolObj.isdate 
                ? this.getInputEle().value
                : event.target.value;
                
            this._currentValue = null;
            this.dispatchEvent(new CustomEvent('change', { detail: {value: val} } ));
            return;
        }
        if (event.key === 'Escape') {
            // Send event to parent with the value
            this.dispatchEvent(new CustomEvent('escape', { detail: '' } ));
        }
    }
    
    // If focused element not within <x-slds-input> ---> Send value-event to parent
    handlePopoverBlur(event) {
        if (this.suppressNativeStyles && this.dtype === 'number') {
            this._inputCl = handleInputCl(this.suppressDefaultStyles, this.suppressNativeStyles);
        }
        if (!this.isPopover) {
            // Check if field is required and send current value of input element
            const ele = event.currentTarget;
            const val = this.getValue();
            const required = ele.required;
            if ( 
                !val && required && (required === 'true' || required === true)
            ) {
                this.dispatchEvent(new CustomEvent('change', { detail: {value: val} } ));                    
            }
            return;
        }

        const val = event.target.value;

        setTimeout(() => {
            const wrapper = this.template.querySelector('[data-id="formElement"]');
            const active = document.activeElement;
    
            if (wrapper && wrapper.contains(active)) {
                // Focus still inside the component
                return;
            }
            if (this.mostRecentKey === 'Enter' || this.mostRecentKey === 'Tab') {
                this._currentValue = null;
                this.dispatchEvent(new CustomEvent('change', { detail: {value: val} } ));
            } else {
                if (this.isPopover) {    
                    if (this._isTabbable && this.mostRecentKey === 'Tab') {
                        console.log('Most recent key was tab and isTabbable is true in handlePopoverBlur() of sldsInput.js');
                    } else {
                        // Focus moved outside the component — signal to close
                        this.dispatchEvent(new CustomEvent('escape', { detail: '' } ));
                    }
                    
                }
            }
        }, 0);
    }

    // Prevent the main input element from losing focus when an item in dropdown is clicked/pressed
    deflectBlur(event) {
        if (!this.isPopover) return;
        event.preventDefault();
    }
    handlePopoverDate(event) {
        event.target.classList.add('slds-is-selected');
        setTimeout(() => {
            const val = this.boolObj.isdatetime 
                ? this.syncDatetimeManual() 
                    : this.boolObj.isdate 
                ? this.getInputEle().value
                : event.target.value;

            const isSame = this.boolObj.isdatetime
                ? this.originalDate?.getTime() === val?.getTime()
                : this.originalDate === val;

            if (isSame)  {
                this.dispatchEvent(new CustomEvent('escape', { detail: '' } ));
            } else {
                this.dispatchEvent(new CustomEvent('change', { detail: {value: val} } ));
            }
            this._currentValue = null;
        }, 120);
    }
    // =================================================================== \\



    // =================== Data Type Conversion Getters ================== \\
    // Return empty string if no val provided
    get textVal() {
        return this._currentValue ?? '';
    }
    get passwordVal() {
        return this._currentValue ? this._currentValue : '';
    }
    get textAreaVal() {
        return this._currentValue ?? '';
    }
    get checkboxVal() {
        return publicPropertyToBool(this._currentValue);
    }
    // Convert currentVal from string to number
    get numberVal() {
        return parseInt(this._currentValue) || null;
    }
    // Convert currentVal from string to two-digit decimal (e.g. 1.99 or 1.00)
    get currencyVal() {
        const n = parseFloat(this._currentValue);
        if (isNaN(n)) {
            return null;          // or however you’d like to handle bad input
        }
        return n.toFixed(2);      // → "1.00", "1.60", etc.
    }
    // Convert currentVal from string to specified decimal (<decimalAmt>) --> defaults to 2 decimals
    get decimalVal() {
        const n = parseFloat(this._currentValue);
        if (isNaN(n)) {
            return null;          // or however you’d like to handle bad input
        }
        return this.decimalAmt  
            ? n.toFixed(this.decimalAmt) 
            : n.toFixed(2);      // → "1.00", "1.60", etc.
    }
    // convert currentVal from String to date
    /*get dateVal() {
        if (!this.currentVal) {
            return null;
        }
        // Try exact YYYY-MM-DD → construct at local midnight
        if (this.currentVal.includes('-')) {
            const [y, m, d] = this.currentVal.split('-').map(Number);
            if (![y, m, d].some(isNaN)) {
              return new Date(y, m - 1, d);
            }
        }

        // Try MM/DD/YYYY
        if (this.currentVal.includes('/')) {
            const [mo, da, yr] = this.currentVal.split('/').map(Number);
            if (![mo, da, yr].some(isNaN)) {
                return new Date(yr, mo - 1, da);
            }
        }

        // Fallback: let the Date parser try
        const fallback = new Date(this.currentVal);
        if (isNaN(fallback.getTime())) {
            return null;
        }

        // strip time portion
        return new Date(
            fallback.getFullYear(),
            fallback.getMonth(),
            fallback.getDate()
        );
    }*/ 
    
    get dateVal() {
        if (!this._currentValue) return '';
    
        const date = new Date(this._currentValue);
    
        if (isNaN(date.getTime())) {
            return ''; // Invalid date string
        }
    
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
    
        return `${y}-${m}-${d}`;  // Format for <input type="date">
    }
    // =================================================================== \\

    handlePasteBlock(e) {
        if (this.blockPaste) {
            e.preventDefault();
        }
    }

    // ================== Data Type Conversion w/ Sync =================== \\
    // date getter: "YYYY-MM-DD" or blank
    get datetimeDateVal() {
        // If user never set anything, maybe you do want “today”?
        if (!this._currentValue) {
            const t = new Date();
            const yy = t.getFullYear();
            const mm = String(t.getMonth()+1).padStart(2,'0');
            const dd = String(t.getDate()).padStart(2,'0');
            return `${yy}-${mm}-${dd}`;
        }
    
        // Parse whatever they gave you
        const d = this.manualGetDatetime(this._currentValue);
    
        // If it’s valid, format it as YYYY-MM-DD using **local** time
        if (d) {
            const yy = d.getFullYear();
            const mm = String(d.getMonth()+1).padStart(2,'0');
            const dd = String(d.getDate()).padStart(2,'0');
            return `${yy}-${mm}-${dd}`;
        }
    
        // If parsing failed (really invalid), clear the input rather than show “today”
        return '';
    }
    // time getter: "HH:mm" from the sync date, or default "10:00"
    get datetimeTimeVal() {
        if (this.datetimeSyncVal) {
            return this.datetimeSyncVal.toTimeString().slice(0, 5);
        }
        return '00:00'; // ← instead of '10:00'
    }
    
    syncDatetimeManual() {
        // grab both controls
        const dateVal = this.template.querySelector('[data-id="datetimeInputDate"]').value;
        const timeVal = this.template.querySelector('[data-id="datetimeInputTime"]').value;

        // still no date selected
        if (!dateVal) return;  

        // build a new Date from "YYYY-MM-DDThh:mm"
        return new Date(`${dateVal}T${timeVal}`);
    }
    manualGetDatetime(dateParam) {
        // 1) If it’s already a Date, use it…
        let d = dateParam instanceof Date
                ? dateParam
                : typeof dateParam === 'string'
                    // 2) …otherwise, try parsing the string
                    ? new Date(dateParam)
                    : null;

        // 3) Bail on totally bogus input
        if (!(d instanceof Date) || isNaN(d.getTime())) {
            console.error('manualGetDatetime: invalid input →', dateParam);
            return null;
        }

        return d;
    }
    // =================================================================== \\

    
    // Apply Styles to the host 👉 Can pass 1.Array of Arrays   2.Array of Object   3.Strings
    _applyVarsDirect(cssVar, varStyle=null, eleAttr=null) {

        if (eleAttr) {
            const ele = this.template.querySelector(cssVar);
            if (ele) {
                ele.style[eleAttr] = varStyle;
            }
            return;
        }

        // Check that CSS var starts with '--' and has a non-null stlye 
        const isValid = (cssVarP, varStyleP) => {
            return typeof cssVarP === 'string' && 
                typeof varStyleP === 'string' &&
                cssVarP?.startsWith('--') && 
                varStyleP;
        }


        if (Array.isArray(cssVar)) {
            // Handle Array of vars
            cssVar.forEach(hostVar => {
                if (Array.isArray(hostVar) && isValid(hostVar[0], hostVar[1])) {
                    // Handle Array structure  (CSS var at index 0)
                    this.style.setProperty(hostVar[0], hostVar[1]);
                } else if (typeof hostVar === 'object' && isValid(hostVar.cssVar, hostVar.varStyle)) {
                    // Handle Object structure (cssVar & varStyle keys)
                    this.style.setProperty(hostVar.cssVar, hostVar.varStyle)
                }
            });
        } else if (isValid(cssVar, varStyle)) {
            // Handle string arguments
            this.style.setProperty(cssVar,  varStyle);
        }
    }


    handleNumberFocus() {
        if (this.suppressNativeStyles) {
            this._inputCl = this._inputCl.replace('slds-input_counter', '');
        }
    }

    toastObj;
    showToast(title, message, variant, mode=null) {
        const modeP = mode 
            ? mode 
                : variant === 'success'
            ? 'dismissible'
            : 'sticky';
        
        this.toastObj = { 
            variant: variant, 
            mode: modeP, 
            message: message,
            header: { 
                text: title
            }        
        };
    }
}